import * as Sentry from '@sentry/react';
import { VStack } from '@televet/kibble-ui';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { ChangeEvent, ReactElement, useCallback, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { firebase } from 'shared/lib';
import { useAuth } from 'shared/providers/AuthProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsMyProfileEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useUpdateChangePasswordModalUserMutation } from 'shared/types/graphql';

interface IChangePasswordModalProps {
  email: string;
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal = ({ email, isOpen, onClose: _onClose }: IChangePasswordModalProps): ReactElement => {
  const toast = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [passwordValidationErrorMessage, setPasswordValidationErrorMessage] = useState('');
  const { clinicUser } = useClinicUser();
  const userId = clinicUser?.id;
  const { isImpersonating } = useAuth();
  const { trackEvent } = useTracking();

  const [updateUser, { loading }] = useUpdateChangePasswordModalUserMutation();

  const onClose = useCallback((): void => {
    trackEvent(SettingsMyProfileEvents.CancelEditUserPassword);
    setNewPassword('');
    setCurrentPassword('');
    setNewPasswordConfirmation('');
    setPasswordValidationErrorMessage('');
    _onClose();
  }, [_onClose, trackEvent]);

  const resetPassword = useCallback((): void => {
    if (newPasswordConfirmation === newPassword) {
      firebase
        .auth()
        .signInWithEmailAndPassword(email, currentPassword)
        .then(() => {
          firebase
            .auth()
            .currentUser?.updatePassword(newPassword)
            .then(() => {
              onClose();
              toast({
                status: 'success',
                title: 'New Password Saved!',
              });
            })
            .catch((error) => {
              Sentry.captureException(error);
              setPasswordValidationErrorMessage(error.message);
            });
        })
        .catch((error): void => {
          Sentry.captureException(error);
          setPasswordValidationErrorMessage(error.message);
        });
    } else {
      setPasswordValidationErrorMessage("Passwords don't match");
    }
  }, [newPassword, newPasswordConfirmation, email, currentPassword, onClose, toast]);

  const resetPasswordWhileImpersonating = useCallback((): void => {
    if (newPasswordConfirmation === newPassword) {
      try {
        updateUser({
          variables: {
            where: { id: userId },
            data: { isNewPasswordRequired: true },
          },
          update: (cache, result) => {
            const user = result.data?.updateOneUser;
            if (user?.id) {
              cache.modify({
                id: cache.identify({ __typename: user.__typename, id: userId }),
                fields: {
                  isNewPasswordRequired() {
                    return false; // Prevents the "Create password modal" from showing right after changing the password
                  },
                },
              });
            }
          },
        });
        firebase
          .auth()
          .currentUser?.updatePassword(newPassword)
          .then(() => {
            onClose();
            toast({
              status: 'success',
              title: 'New Password Saved!',
            });
          })
          .catch((error) => {
            Sentry.captureException(error);
            setPasswordValidationErrorMessage(error.message);
          });
      } catch (error) {
        toast({
          status: 'error',
          title: "Unable to change user's password!",
        });
      }
    } else {
      setPasswordValidationErrorMessage("Passwords don't match");
    }
  }, [newPasswordConfirmation, newPassword, updateUser, userId, onClose, toast]);

  const onSubmit = useCallback((): void => {
    trackEvent(SettingsMyProfileEvents.EditUserPasswordConfirmed);
    if (isImpersonating) {
      resetPasswordWhileImpersonating();
    } else {
      resetPassword();
    }
  }, [trackEvent, isImpersonating, resetPasswordWhileImpersonating, resetPassword]);

  return (
    <Modal size="sm" isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Change Password</ModalHeader>
      <ModalCloseButton />

      <ModalBody>
        <VStack spacing={2} mb={2}>
          {isImpersonating ? (
            <>
              Changing passwords while impersonating will create a one time password. User will be required to create a
              new password on the next session
            </>
          ) : (
            <TextInput
              type="password"
              label="Current password"
              autoComplete="off"
              onBlur={(): void => {
                trackEvent(SettingsMyProfileEvents.EnterCurrentPassword);
              }}
              value={currentPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setCurrentPassword(e.target.value);
              }}
            />
          )}
          <TextInput
            type="password"
            label="New password"
            autoComplete="off"
            onBlur={(): void => {
              trackEvent(SettingsMyProfileEvents.EnterNewPassword);
            }}
            value={newPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setNewPassword(e.target.value);
            }}
          />
          <TextInput
            type="password"
            label="Confirm new password"
            autoComplete="off"
            onBlur={(): void => {
              trackEvent(SettingsMyProfileEvents.ConfirmNewPassword);
            }}
            value={newPasswordConfirmation}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setNewPasswordConfirmation(e.target.value);
            }}
          />
        </VStack>
        <Text textColor="text.danger" size="xs">
          {passwordValidationErrorMessage}
        </Text>
      </ModalBody>
      <ModalFooterButtons onCancel={onClose} onSubmit={onSubmit} isLoading={loading} submitText="Save" />
    </Modal>
  );
};

export default ChangePasswordModal;
